/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { colors, fonts, mixins } from '@blend/consumer-components';
import { BlIcon, BlIconType, SubstringHighlight } from '@blend/shared-react';

import { FormattedAddress } from './addressDropdownTypes';
import { LinkButtonWrapper } from '@components/LinkButton/LinkButtonWrapper';
import { LinkButtonContentWrapper } from '@components/LinkButton/LinkButtonContentWrapper';
import { LinkButtonTextWrapper } from '@components/LinkButton/LinkButtonTextWrapper';

const selectedElementBackground = colors.snowPlus3;

const dropdownElementStyle = StyleSheet.create({
  wrapper: {
    listStyle: 'none',
    ':not(:last-child)': {
      borderBottom: `1px solid ${colors.gray}`,
    },
    ':hover': {
      backgroundColor: selectedElementBackground,
    },
  },
  isHighlighted: {
    backgroundColor: selectedElementBackground,
  },
  buttonContent: {
    padding: 10,
    display: 'flex',
    alignItems: 'center',
  },
  iconWrapper: {
    color: colors.clayPlus4,
    alignItems: 'center',
    marginTop: 10,
  },
  addressWrapper: {
    overflow: 'hidden',
  },
  addressLine: {
    ...mixins.textOverflow(),
  },
  firstAddressLine: {
    ...fonts.UntitledSans,
    fontSize: 16,
    fontWeight: 400,
    color: colors.clayPlus4,
  },
  secondAddressLine: {
    ...fonts.UntitledSans,
    fontWeight: 400,
    fontSize: 12,
    color: colors.clayPlus4,
  },
  locationDiv: {
    display: 'flex',
    marginTop: 10,
    paddingLeft: 13,
    paddingRight: 13,
    paddingBottom: 10,
    paddingUp: 10,
  },
  addressDiv: {
    marginLeft: 13,
  },
  showOnFocus: {
    ...fonts.Roboto,
    textDecorationLine: 'none',
  },
});

const addressDropdownElementLineStyle = StyleSheet.create({
  match: {
    fontWeight: fonts.weight.semibold,
    ':not(:empty)': {
      marginRight: 4,
    },
  },
  wrapper: {
    display: 'flex',
  },
});

interface AddressDropdownElementLineProps {
  text: string;
  searchText: string;
}

const AddressDropdownElementLine: React.FC<AddressDropdownElementLineProps> = ({ text, searchText }) => (
  <span className={css(addressDropdownElementLineStyle.wrapper)}>
    <SubstringHighlight text={text} substring={searchText} />
  </span>
);

interface AddressDropdownElementProps extends FormattedAddress {
  searchText: string;
  highlighted: boolean;
  icon: BlIconType;
  onSelect: () => void;
}

const AddressDropdownElement = ({
  searchText,
  firstLine,
  secondLine,
  highlighted,
  icon,
  onSelect,
}: AddressDropdownElementProps) => {
  const wrapperClasses = [highlighted && dropdownElementStyle.isHighlighted, dropdownElementStyle.wrapper];

  return (
    <li className={css(...wrapperClasses)}>
      <LinkButtonWrapper onMouseDown={onSelect} data-it="AddressDropdownElement">
        <LinkButtonContentWrapper isInline isInlineText>
          <div className={css(dropdownElementStyle.locationDiv)}>
            <span className={css(dropdownElementStyle.iconWrapper)}>
              <BlIcon size="md" icon={icon} />
            </span>
            <div className={css(dropdownElementStyle.addressDiv)}>
              <LinkButtonTextWrapper className={css(dropdownElementStyle.showOnFocus)} isInline isInlineText>
                <span className={css(dropdownElementStyle.addressLine, dropdownElementStyle.firstAddressLine)}>
                  <AddressDropdownElementLine text={firstLine} searchText={searchText} />
                </span>
                <span className={css(dropdownElementStyle.addressLine, dropdownElementStyle.secondAddressLine)}>
                  <AddressDropdownElementLine text={secondLine} searchText={searchText} />
                </span>
              </LinkButtonTextWrapper>
            </div>
          </div>
        </LinkButtonContentWrapper>
      </LinkButtonWrapper>
    </li>
  );
};

export default AddressDropdownElement;
